/**
 * Agent status from the Custodian API (ENG-271, "evaluate the Custodian API").
 *
 * The live feed publishes no agent stream, so the Agents tab and Overview used
 * to show blanks. Custodian does know two things per aircraft: whether its
 * adapter is connected (`/health`) and which GCS plugin it is bound to (the
 * inventory record). Same semantics as the Inventory panel, which derives the
 * plugin's online state from `adapter_connected` too.
 */
import type { CustodianHealth } from '@/lib/api/custodian';
import type { InventoryDrone } from '@/lib/api/types';

export type AgentTone = 'ok' | 'warn' | 'crit' | 'muted';

export interface CustodianAgentView {
  name: string;
  sub: string;
  status: string;
  tone: AgentTone;
  lastMsgAt: string | null;
  latencyMs: number | null;
}

const real = (v: string | null | undefined): v is string => !!v && v !== '—' && v !== 'none';

/** Aircraft agent: the adapter feeding telemetry for this aircraft. */
export function aircraftAgentFromCustodian(
  drone: InventoryDrone | undefined,
  health: CustodianHealth | undefined,
  pipelineDelayS: number | null | undefined,
): CustodianAgentView | null {
  if (!health) return null;
  const rate = drone && drone.publish_rate_hz > 0 ? ` · ${drone.publish_rate_hz} Hz` : '';
  return {
    name: 'Aircraft Agent',
    sub: `${real(drone?.personality) ? drone?.personality : 'Adapter'}${rate}`,
    status: health.adapter_connected ? 'Connected' : 'Disconnected',
    tone: health.adapter_connected ? 'ok' : 'crit',
    lastMsgAt: health.last_telemetry_at,
    latencyMs: pipelineDelayS != null ? Math.round(pipelineDelayS * 1000) : null,
  };
}

/** GCS agent: the GCS plugin the aircraft is bound to, with its version. */
export function gcsAgentFromCustodian(
  drone: InventoryDrone | undefined,
  health: CustodianHealth | undefined,
): CustodianAgentView | null {
  if (!drone) return null;
  const plugin = real(drone.gcs_plugin) ? drone.gcs_plugin : null;
  if (!plugin) {
    return {
      name: 'GCS Agent',
      sub: '—',
      status: 'Not configured',
      tone: 'muted',
      lastMsgAt: null,
      latencyMs: null,
    };
  }
  const online = health ? health.adapter_connected : drone.gcs_plugin_status === 'online';
  return {
    name: `GCS Agent — ${plugin}`,
    sub: health?.plugin_version ? `plugin v${health.plugin_version}` : 'plugin',
    status: online ? 'Online' : 'Offline',
    tone: online ? 'ok' : 'crit',
    lastMsgAt: null,
    latencyMs: null,
  };
}
